/*
  Shopify path: assets/beulah-cart-drawer.js
  Beulah Style — slide-out mini cart (cart drawer).

  Markup + styles live in snippets/cart-drawer.liquid (rendered once from
  layout/theme.liquid); this file fills it with the live cart and wires it up.
  Content is built client-side from Shopify's /cart.js JSON, so there is one
  source of truth (the real cart) and no duplicate Liquid to keep in sync.

  WHAT OPENS IT (only when something is ADDED to the bag)
  1. A successful Quick add (assets/beulah-quickadd.js fires "bs:item-added").
  2. The product page "Add to bag" form: any <form action=".../cart/add"> is
     intercepted (capture phase), posted to /cart/add.js with fetch instead of
     navigating to the cart page, then the drawer opens. If the fetch itself
     fails (offline etc.) the form is submitted the normal way as a fallback.
     On mobile the PDP's own option sheet is closed first (by clicking its own
     close button) so it doesn't stay open behind the drawer.

  WHAT DOES *NOT* OPEN IT: the header cart icon / "Cart (n)" link. An earlier
  version also intercepted those clicks to open the drawer; per merchant
  request they now behave normally and go to the /cart page, so the drawer is
  purely an "item added" confirmation. (The drawer's own "View bag" button
  still links to /cart.)

  WHAT IT DOES
  - Qty +/-, Remove (qty 0) via /cart/change.js using the line item KEY, so
    items with different properties never collide. Stock errors from Shopify
    (422) are shown in a red banner at the top of the drawer.
  - Mirrors the cart page: free-shipping bar against the SAME $200 threshold
    (data-free-ship-cents on the drawer, 20000) measured on cart.total_price
    (after discounts, like the cart page); subtotal is original_total_price
    (before discounts) with a separate highlighted discount row, using the
    same wording as sections/main-cart-items.liquid; same empty-bag copy.
  - Keeps the header cart count in sync after every change.

  NAMING GOTCHA (this was a real bug): the drawer element's id is
  "bsCartDrawer", and browsers automatically expose any element with an id as
  a same-named property on `window`. The first version of this file started
  with `if (window.bsCartDrawer) return;` as a "already loaded" guard — which
  was always TRUE because window.bsCartDrawer was the <aside> element itself,
  so the script quit on its first line and wired up nothing. The guard flag
  and the public API now use names that don't collide with any element id:
  window.__bsCartDrawerLoaded and window.bsCart.

  Public API: window.bsCart = { open, close, refresh }.
*/
(function () {
  if (window.__bsCartDrawerLoaded) return;
  var drawer = document.getElementById('bsCartDrawer');
  var overlay = document.getElementById('bsCartOverlay');
  if (!drawer || !overlay) return;
  window.__bsCartDrawerLoaded = true;

  var ROOT = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  var bodyEl = drawer.querySelector('.bs-cd-body');
  var footEl = drawer.querySelector('.bs-cd-foot');
  var shipEl = drawer.querySelector('.bs-cd-ship');
  var countEl = drawer.querySelector('.bs-cd-count');
  var cartUrl = drawer.getAttribute('data-cart-url') || (ROOT + 'cart');
  var allUrl = drawer.getAttribute('data-all-url') || (ROOT + 'collections/all');
  var threshold = parseInt(drawer.getAttribute('data-free-ship-cents'), 10) || 20000;
  var prevOverflow = '';
  var isOpen = false;
  var busy = false;
  var errorMsg = '';

  function esc(s) {
    return String(s == null ? '' : s)
      .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;').replace(/'/g, '&#39;');
  }

  function money(cents, currency) {
    try {
      return new Intl.NumberFormat('en-US', { style: 'currency', currency: currency || 'USD' }).format(cents / 100);
    } catch (e) {
      return '$' + (cents / 100).toFixed(2);
    }
  }

  function sized(url, w) {
    return url + (url.indexOf('?') > -1 ? '&' : '?') + 'width=' + w;
  }

  function getCart() {
    return fetch(ROOT + 'cart.js', { headers: { Accept: 'application/json' } }).then(function (r) { return r.json(); });
  }

  function updateHeaderCount(n) {
    document.querySelectorAll('a[href$="/cart"]').forEach(function (a) {
      if (a.classList.contains('bs-label-link') && /^\s*Cart/i.test(a.textContent)) {
        a.textContent = 'Cart (' + n + ')';
      } else if (a.getAttribute('aria-label') === 'Cart') {
        var badge = a.querySelector('.bs-cart-count');
        if (n > 0) {
          if (!badge) { badge = document.createElement('span'); badge.className = 'bs-cart-count'; a.appendChild(badge); }
          badge.textContent = n;
        } else if (badge) {
          badge.remove();
        }
      }
    });
  }

  function render(cart) {
    var n = cart.item_count;
    countEl.textContent = n ? '(' + n + ')' : '';
    updateHeaderCount(n);

    var errHtml = errorMsg ? '<div class="bs-cd-error">' + esc(errorMsg) + '</div>' : '';

    if (!n) {
      shipEl.innerHTML = '';
      footEl.innerHTML = '';
      bodyEl.innerHTML = errHtml + '<div class="bs-cd-empty">Your bag is quietly empty.<div><a class="bs-btn" href="' + esc(allUrl) + '">Shop New Arrivals</a></div></div>';
      return;
    }

    if (cart.total_price >= threshold) {
      shipEl.innerHTML = '<p>You&rsquo;ve unlocked free shipping! &#10003;</p><div class="bs-cd-track"><div class="bs-cd-fill" style="width:100%"></div></div>';
    } else {
      var pct = Math.max(0, Math.min(100, Math.floor(cart.total_price * 100 / threshold)));
      shipEl.innerHTML = '<p>You are ' + money(threshold - cart.total_price, cart.currency) + ' away from FREE shipping!</p><div class="bs-cd-track"><div class="bs-cd-fill" style="width:' + pct + '%"></div></div>';
    }

    var itemsHtml = cart.items.map(function (it) {
      var variant = it.variant_title && it.variant_title !== 'Default Title' ? '<div class="bs-cd-variant">' + esc(it.variant_title) + '</div>' : '';
      var img = it.image ? '<img src="' + esc(sized(it.image, 240)) + '" alt="' + esc(it.product_title) + '">' : '';
      var price = '';
      if (it.original_line_price > it.final_line_price) price += '<s>' + money(it.original_line_price, cart.currency) + '</s>';
      price += money(it.final_line_price, cart.currency);
      return '<div class="bs-cd-item" data-key="' + esc(it.key) + '" data-qty="' + it.quantity + '">' +
        '<a class="bs-cd-img" href="' + esc(it.url) + '">' + img + '</a>' +
        '<div class="bs-cd-info">' +
          '<a class="bs-cd-name" href="' + esc(it.url) + '">' + esc(it.product_title) + '</a>' + variant +
          '<div class="bs-cd-row2">' +
            '<div class="bs-cd-qty"><button type="button" data-cd-minus aria-label="Decrease quantity">&minus;</button><span>' + it.quantity + '</span><button type="button" data-cd-plus aria-label="Increase quantity">+</button></div>' +
            '<div class="bs-cd-price">' + price + '</div>' +
          '</div>' +
          '<button type="button" class="bs-cd-remove" data-cd-remove>Remove</button>' +
        '</div></div>';
    }).join('');
    bodyEl.innerHTML = errHtml + itemsHtml;

    var discountTotal = 0;
    (cart.cart_level_discount_applications || []).forEach(function (d) { discountTotal += d.total_allocated_amount || 0; });
    var discountHtml = discountTotal > 0
      ? '<div class="bs-cd-discount"><span>10% off applied, thank you for signing up! &#10003;</span><span>&minus;' + money(discountTotal, cart.currency) + '</span></div>'
      : '';

    footEl.innerHTML =
      '<div class="bs-cd-row"><span>Subtotal</span><span>' + money(cart.original_total_price, cart.currency) + '</span></div>' +
      discountHtml +
      '<div class="bs-cd-row muted"><span>Shipping</span><span>Calculated at checkout</span></div>' +
      '<div class="bs-cd-row total"><span>Total</span><span>' + money(cart.total_price, cart.currency) + '</span></div>' +
      '<a class="bs-btn" href="' + esc(ROOT + 'checkout') + '">Checkout</a>' +
      '<a class="bs-btn-outline" href="' + esc(cartUrl) + '">View bag</a>';
  }

  function show() {
    if (isOpen) return;
    isOpen = true;
    prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    overlay.classList.add('is-open');
    drawer.classList.add('is-open');
    drawer.setAttribute('aria-hidden', 'false');
    var closeBtn = drawer.querySelector('.bs-cd-close');
    if (closeBtn) setTimeout(function () { closeBtn.focus(); }, 60);
  }

  function hide() {
    if (!isOpen) return;
    isOpen = false;
    errorMsg = '';
    document.body.style.overflow = prevOverflow || '';
    overlay.classList.remove('is-open');
    drawer.classList.remove('is-open');
    drawer.setAttribute('aria-hidden', 'true');
  }

  function refresh() {
    return getCart().then(render);
  }

  function open() {
    return getCart().then(function (cart) {
      render(cart);
      show();
    }).catch(function () {
      window.location.href = cartUrl;
    });
  }

  function changeLine(key, qty) {
    if (busy) return;
    busy = true;
    errorMsg = '';
    drawer.classList.add('is-busy');
    fetch(ROOT + 'cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ id: key, quantity: qty })
    })
      .then(function (r) { return r.json().then(function (d) { return { ok: r.ok, d: d }; }); })
      .then(function (res) {
        if (!res.ok) {
          errorMsg = (res.d && (res.d.description || res.d.message)) || 'Could not update your bag.';
          return getCart().then(render);
        }
        render(res.d);
      })
      .catch(function () {
        errorMsg = 'Could not update your bag. Please try again.';
        return getCart().then(render).catch(function () {});
      })
      .then(function () {
        busy = false;
        drawer.classList.remove('is-busy');
      });
  }

  drawer.addEventListener('click', function (e) {
    var t = e.target;
    if (!t || !t.closest) return;
    if (t.closest('.bs-cd-close')) { hide(); return; }
    var item = t.closest('.bs-cd-item');
    if (!item) return;
    var key = item.getAttribute('data-key');
    var qty = parseInt(item.getAttribute('data-qty'), 10) || 0;
    if (t.closest('[data-cd-plus]')) changeLine(key, qty + 1);
    else if (t.closest('[data-cd-minus]')) changeLine(key, Math.max(0, qty - 1));
    else if (t.closest('[data-cd-remove]')) changeLine(key, 0);
  });

  overlay.addEventListener('click', hide);
  document.addEventListener('keydown', function (e) {
    if (e.key === 'Escape' && isOpen) hide();
  });

  document.addEventListener('bs:item-added', function () { open(); });

  /* product page "Add to bag": post with fetch, then open the drawer */
  document.addEventListener('submit', function (e) {
    var form = e.target;
    if (!form || !form.matches || !form.matches('form[action*="/cart/add"]')) return;
    e.preventDefault();

    var params = new URLSearchParams();
    new FormData(form).forEach(function (v, k) {
      if (k === 'id' || k === 'quantity' || k === 'selling_plan' || k.indexOf('properties[') === 0) params.append(k, v);
    });
    if (!params.has('quantity')) params.append('quantity', '1');

    var btn = form.querySelector('[type="submit"]');
    var oldText = btn ? btn.textContent : '';
    if (btn) { btn.disabled = true; btn.textContent = 'Adding...'; }

    fetch(ROOT + 'cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/x-www-form-urlencoded', Accept: 'application/json' },
      body: params.toString()
    })
      .then(function (r) { return r.json().then(function (d) { return { ok: r.ok, d: d }; }); })
      .then(function (res) {
        if (btn) { btn.disabled = false; btn.textContent = oldText; }
        if (!res.ok) {
          errorMsg = (res.d && (res.d.description || res.d.message)) || 'Could not add to bag.';
        } else {
          errorMsg = '';
        }
        if (form.classList.contains('is-mobile-open')) {
          var c = form.querySelector('.pdp-drawer-close');
          if (c) c.click();
        }
        return open();
      })
      .catch(function () {
        if (btn) { btn.disabled = false; btn.textContent = oldText; }
        form.submit();
      });
  }, true);

  window.bsCart = { open: open, close: hide, refresh: refresh };
})();
